import { useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { Redirect, useHistory } from 'react-router-dom';
import { useAuth } from '@/lib/auth/useAuth';
import { claimHandle, handleSchema } from '@/lib/api/profiles';
import { Logo } from '@/components/ui/Logo';
import { ZodError } from 'zod';

export default function ClaimHandlePage() {
  const { status, user, signOut } = useAuth();
  const history = useHistory();
  const [handle, setHandle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (status === 'unauthenticated') return <Redirect to="/auth" />;
  if (status === 'authenticated') return <Redirect to="/" />;

  const validationError = handle.length > 0
    ? (() => { try { handleSchema.parse(handle); return null; } catch (e) { return e instanceof ZodError ? e.errors[0].message : null; } })()
    : null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!handle.trim() || validationError || !user) return;
    setLoading(true);
    setError(null);
    try {
      await claimHandle(handle.trim());
      history.replace('/');
    } catch (err: unknown) {
      // Supabase throws PostgrestError objects (not native Error instances)
      const pgErr = err as { message?: string; code?: string };
      const code = pgErr?.code ?? '';
      const msg = pgErr?.message ?? (err instanceof Error ? err.message : 'Something went wrong.');
      if (code === '23505' || msg.includes('unique') || msg.includes('already')) {
        setError('That handle is taken. Try another one.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <IonPage>
      <IonContent scrollY={false}>
        <div className="flex min-h-full flex-col items-center justify-center bg-[#faf9f7] px-6 py-16">
          <div className="w-full max-w-sm">
            <div className="mb-8 flex justify-center">
              <Logo size="lg" />
            </div>

            <h1 className="mb-2 text-center font-serif text-2xl font-bold text-gray-900">
              Choose your handle
            </h1>
            <p className="mb-8 text-center text-sm text-gray-500">
              Your handle is your public identity on Open iPub. You can't change it later.
            </p>

            <form onSubmit={submit} className="flex flex-col gap-3">
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">@</span>
                <input
                  type="text"
                  value={handle}
                  onChange={e => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                  placeholder="yourhandle"
                  maxLength={30}
                  autoFocus
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className="w-full rounded-lg border border-gray-200 py-3 pl-8 pr-4 text-sm outline-none transition-colors focus:border-[#1a56a0] focus:ring-1 focus:ring-[#1a56a0]"
                />
              </div>

              {handle.length > 0 && (
                <p className={`text-xs ${validationError ? 'text-red-500' : 'text-green-600'}`}>
                  {validationError ?? '✓ Looks good'}
                </p>
              )}

              {error && (
                <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading || !handle.trim() || validationError !== null}
                className="rounded-lg bg-[#2f3e46] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1e2c33] disabled:opacity-50"
              >
                {loading ? 'Claiming…' : 'Claim handle'}
              </button>
            </form>

            <p className="mt-4 text-center text-xs text-gray-400">
              3–30 characters, lowercase letters, numbers, hyphens, or underscores.
            </p>

            <button
              onClick={() => signOut()}
              className="mt-6 block w-full text-center text-xs text-gray-400 hover:text-gray-600"
            >
              Sign out
            </button>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
